import { useState, useMemo } from 'react'
import { subscriptionsService, ACCOUNT_STATUS_LABELS } from '../services/subscriptionsService'
import { useDataFetch } from '../hooks/useDataFetch'
import { formatDate } from '../utils/helpers'

const STATUS_FILTERS = ['all', 'active_trial', 'active_trial_end_scheduled', 'expired_trial', 'active_paid', 'active_paid_suspension_scheduled', 'suspended']

/**
 * Subscriptions Page - Enterprise Standard
 * Financial management view.
 */
export default function Subscriptions() {
    const { data: subscriptions, loading, error, refetch } = useDataFetch(subscriptionsService.getAll)
    const [statusFilter, setStatusFilter] = useState('all')

    const filteredSubscriptions = useMemo(() => {
        if (!subscriptions) return []
        if (statusFilter === 'all') return subscriptions
        return subscriptions.filter((sub) => sub.status === statusFilter)
    }, [subscriptions, statusFilter])

    const stats = useMemo(() => {
        if (!subscriptions) return { total: 0, active: 0, cancelled: 0, mrr: 0 }
        return {
            total: subscriptions.length,
            // Active Customers = fully active, including a scheduled suspension
            // (paid access continues in full until the effective suspension date).
            active: subscriptions.filter((s) => s.isFullyActive).length,
            cancelled: subscriptions.filter((s) => s.status === 'suspended').length,
            // MRR = active_paid only (suspension-scheduled still pays until period end,
            // but excluded here for a conservative forward-looking figure)
            mrr: subscriptions.filter((s) => s.status === 'active_paid').reduce((sum, s) => sum + s.amount, 0),
        }
    }, [subscriptions])

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h1 className="text-2xl font-bold text-admin-heading tracking-tight">Subscriptions</h1>
                <div className="flex items-center gap-2">
                    <button onClick={refetch} className="btn-secondary text-xs">
                        Sync Stripe
                    </button>
                    <button className="btn-primary text-xs">
                        Export Report
                    </button>
                </div>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <MetricCard label="Total Subscriptions" value={stats.total} />
                <MetricCard label="Active Customers" value={stats.active} highlight />
                <MetricCard label="Cancelled" value={stats.cancelled} />
                <MetricCard label="Monthly Recurring Revenue" value={`£${stats.mrr.toLocaleString()}`} />
            </div>

            {/* Main Content */}
            <div className="card-admin">
                {/* Filters */}
                <div className="px-5 py-4 border-b border-admin-border flex items-center justify-between bg-gray-50">
                    <div className="flex items-center gap-4">
                        <span className="text-sm font-bold text-admin-muted uppercase tracking-wide">Account Status:</span>
                        <div className="flex gap-2 flex-wrap">
                            {STATUS_FILTERS.map(status => (
                                <button
                                    key={status}
                                    onClick={() => setStatusFilter(status)}
                                    className={`px-3 py-1 text-xs font-bold rounded-full border transition-all ${statusFilter === status
                                        ? 'bg-brand-navy text-white border-brand-navy shadow-sm'
                                        : 'bg-white text-admin-muted border-admin-border hover:border-brand-navy hover:text-brand-navy'
                                        }`}
                                >
                                    {status === 'all' ? 'All' : ACCOUNT_STATUS_LABELS[status]}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Table */}
                <div className="overflow-x-auto">
                    <table className="table-admin">
                        <thead>
                            <tr>
                                <th className="px-6 py-3">Organisation</th>
                                <th className="px-6 py-3">Plan Name</th>
                                <th className="px-6 py-3">Status</th>
                                <th className="px-6 py-3 text-right">Amount</th>
                                <th className="px-6 py-3 text-right">Next Billing / Access Until</th>
                                <th className="px-6 py-3 text-right">Cancelled At</th>
                                <th className="px-6 py-3">Suspension Reason</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100 bg-white">
                            {loading ? (
                                <tr><td colSpan={7} className="p-6 text-center text-admin-muted font-bold">Loading billing data...</td></tr>
                            ) : filteredSubscriptions.length === 0 ? (
                                <tr><td colSpan={7} className="p-6 text-center text-admin-muted font-bold">No subscriptions found.</td></tr>
                            ) : (
                                filteredSubscriptions.map((sub) => (
                                    <tr key={sub.id} className="hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-3 font-bold text-brand-navy">
                                            {sub.organisation}
                                            {sub.isInternal && <span className="ml-2 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded bg-amber-100 text-amber-800 align-middle" title="Excluded from customer and commercial metrics">Internal / test</span>}
                                        </td>
                                        <td className="px-6 py-3 text-admin-text font-medium">{sub.plan}</td>
                                        <td className="px-6 py-3">
                                            <StatusBadge status={sub.status} label={sub.statusLabel} />
                                        </td>
                                        <td className="px-6 py-3 text-right font-bold text-admin-text">
                                            £{sub.amount}
                                        </td>
                                        <td className="px-6 py-3 text-right text-admin-muted font-mono text-xs">
                                            {sub.status === 'active_paid_suspension_scheduled' && sub.suspensionEffectiveAt
                                                ? <span className="text-amber-600 font-semibold">{formatDate(sub.suspensionEffectiveAt)}</span>
                                                : sub.status === 'suspended' && sub.accessUntil
                                                    ? <span className="text-amber-600 font-semibold">{formatDate(sub.accessUntil)}</span>
                                                    : formatDate(sub.nextBilling)
                                            }
                                        </td>
                                        <td className="px-6 py-3 text-right text-admin-muted font-mono text-xs">
                                            {sub.cancelledAt ? formatDate(sub.cancelledAt) : '—'}
                                        </td>
                                        <td className="px-6 py-3 text-admin-text text-xs max-w-[240px] truncate" title={sub.suspensionReason || undefined}>
                                            {sub.suspensionReason || '—'}
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    )
}

function MetricCard({ label, value, highlight }) {
    return (
        <div className="card-admin p-5">
            <p className="text-xs font-bold text-admin-muted uppercase tracking-wider">{label}</p>
            <p className={`text-2xl font-bold mt-2 tracking-tight ${highlight ? 'text-status-success' : 'text-brand-navy'}`}>{value}</p>
        </div>
    )
}

function StatusBadge({ status, label }) {
    const styles = {
        active_trial: 'badge-warning',
        active_trial_end_scheduled: 'badge-warning',
        expired_trial: 'badge-neutral',
        active_paid: 'badge-success',
        active_paid_suspension_scheduled: 'badge-warning',
        suspended: 'badge-neutral',
    }
    const style = styles[status] || 'badge-neutral'

    return (
        <span className={`badge ${style}`}>
            {label || status}
        </span>
    )
}
